/**
 * Lo que la ficha nueva (etapa 4) necesita del documento argus.hallazgos/1, sin redactar nada: agrupa, cuenta y elige textos del
 * catálogo. El significado (nivel, frases, dirección, identidad) es de la base (nexo.hallazgos); aquí no hay reglas propias.
 */
import type { Fuente, Hallazgo, Hallazgos } from '@piezas';
import type { EquipoFicha } from '../api/types';
import { ROTULOS } from '@piezas';
import { FICHA, SAT_REP, texto, tipoSujeto, TIEMPO_SAT, VOCES_SAT } from '@rotulos';

type Valor = Hallazgos['nivel']['valor'];

/**
 * Color del nivel, uno por grado de la escala de riesgo (Bernardo, 2026-10-06; antes, decisión 7 del diseño 4a §3.2): Muy alto
 * (no_operar y muy_grave) rojo sólido; Alto (grave) borde rojo; Medio (revisar) y «Por confirmar» ámbar (Bernardo, 2026-10-06: un falso negativo es peor); Bajo gris. Sale del valor
 * del enum, no del texto. Nexo lo importa de aquí.
 */
export type TonoNivel = 'solido' | 'borde' | 'ambar' | 'gris';
export const tonoNivel = (v: Valor): TonoNivel => (v === 'no_operar' || v === 'muy_grave' ? 'solido' : v === 'grave' ? 'borde' : v === 'revisar' || v === 'por_confirmar' ? 'ambar' : 'gris');

// «Si se confirma: [nivel]» (texto 29): el rótulo viene en cada hueco (`si_se_confirma_etiqueta`, corte 4b, migración
// 20261004200000). La copia temporal de la etapa 4a (registrada en AGENTS.md) se retiró con ella.

/** El episodio de un certificado del SAT (20261016230000): no es de un acta. */
const deCertificado = (e: Hallazgo['episodios'][number]) => e.papel === 'representante_sat';

/**
 * Texto 14: según las voces de las frases del camino (actas, lista de OFAC o las dos). Corte 2 de los certificados: un episodio de un
 * certificado del SAT no cuenta como de un acta, y los certificados se nombran (VOCES_SAT, aprobadas el 2026-10-16).
 */
export function vocesDelCamino(doc: Hallazgos): string | null {
  const voces = new Set(doc.hallazgos.flatMap(h => h.frases.map(f => f.voz)));
  const actas = voces.has('acta') || doc.hallazgos.some(h => h.episodios.some(e => !deCertificado(e))), ofac = voces.has('lista_ofac');
  const sat = voces.has('certificado_sat') || doc.hallazgos.some(h => h.episodios.some(deCertificado));
  if (sat) return actas && ofac ? VOCES_SAT.todas : actas ? VOCES_SAT.actasYSat : ofac ? VOCES_SAT.ofacYSat : VOCES_SAT.sat;
  return actas && ofac ? FICHA.segunActasYOfac : actas ? FICHA.segunActas : ofac ? FICHA.segunOfac : null;
}

/** El subtítulo de la línea de tiempo: con un episodio de un certificado del SAT, lo dice (TIEMPO_SAT, aprobados el 2026-10-16). */
export function subtituloTiempo(doc: Hallazgos): string {
  const episodios = doc.hallazgos.flatMap(h => h.episodios);
  if (!episodios.some(deCertificado)) return ROTULOS.subtituloTiempo;
  return episodios.some(e => !deCertificado(e)) ? TIEMPO_SAT.actasYSat : TIEMPO_SAT.sat;
}

/**
 * Las contrapartes con relaciones en las actas, para «Relaciones con [nombre] en las actas» (texto 32): una contraparte que sólo se
 * relaciona por certificados del SAT no tiene actas que pedir (el detalle de sus certificados es «Ver todos los certificados»).
 */
export const conActas = (g: { hallazgos: Hallazgo[] }) => g.hallazgos.some(h => h.relacion.tipo !== 'representa_ante_sat' && h.relacion.tipo !== 'sancionado_lo_representa_ante_sat');

/** Los hallazgos por contraparte (principio 6 del contrato), en el orden de su hallazgo de más peso. */
export function porContraparte(doc: Hallazgos): { ref: string; nombre: string; hallazgos: Hallazgo[] }[] {
  const grupos = new Map<string, { ref: string; nombre: string; hallazgos: Hallazgo[] }>();
  for (const h of [...doc.hallazgos].sort((a, b) => a.peso - b.peso)) {
    const g = grupos.get(h.contraparte.ref) ?? { ref: h.contraparte.ref, nombre: h.contraparte.nombre, hallazgos: [] };
    g.hallazgos.push(h);
    grupos.set(h.contraparte.ref, g);
  }
  return [...grupos.values()];
}

/** Todas las fuentes de actas del documento (datos, episodios, frases, tramos), para contar las actas del detalle (texto 31). */
export function actasDelDocumento(doc: Hallazgos): number {
  const claves = new Set<string>();
  const ver = (f: Fuente | null | undefined) => {
    if (!f || f.tipo !== 'acta') return;
    claves.add(f.documento ? `d:${f.documento}` : f.llave && 'actoId' in f.llave ? `a:${f.llave.actoId}` : `c:${f.fecha ?? ''}:${f.cita ?? ''}`);
  };
  doc.sujeto.datos.forEach(d => ver(d.fuente));
  for (const h of doc.hallazgos) {
    h.episodios.forEach(e => ver(e.fuente));
    h.frases.forEach(f => ver(f.fuente));
    for (const p of h.relacion.a_traves_de) { ver(p.fuente); ver(p.siguiente?.fuente); ver(p.en_lista.cuenta?.fuente); }
  }
  ver(doc.en_lista.cuenta?.fuente);
  return claves.size;
}

/**
 * Lo que el equipo decidió de este registro (texto 37), o que no se pudo leer (pendiente m4). null: sin decisión, o una unión.
 * Etapa 5 (Bernardo, 2026-10-06): la ficha de lo que el equipo unió se ve como una sola persona y nada en ella anuncia un grupo; la unión
 * se ve en «¿Es la misma persona?» («Lo decidido») y en «Decisión». Sólo «es otra» se dice aquí.
 */
export function textoEquipo(doc: Hallazgos, equipo: EquipoFicha | null, leido: boolean | undefined): string | null {
  if (leido === false) return FICHA.equipoNoLeido;
  if (!equipo) return null;
  if (equipo.verdict === 'distinta' || equipo.role === 'distinta') return texto(FICHA.equipoOtra, { tipo: tipoSujeto(doc) });
  return null;
}

/** Texto 35: personas o empresas del registro corroboradas con esta entidad que pasan del tope de 9. */
export function textoMasPersonas(doc: Hallazgos): string | null {
  const n = doc.omitido.personas_registro ?? 0;
  if (n <= 0) return null;
  const tipo = tipoSujeto(doc);
  return n === 1 ? texto(FICHA.masPersonasUna, { tipo }) : texto(FICHA.masPersonasVarias, { n, tipo, tipos: `${tipo}s` });
}

/** Texto 34: huecos que no cupieron en el tope. */
export function textoMasHuecos(doc: Hallazgos): string | null {
  const n = doc.omitido.huecos ?? 0;
  return n <= 0 ? null : n === 1 ? FICHA.masHuecosUno : texto(FICHA.masHuecosVarios, { n });
}

/** El uuid de la entidad de una contraparte (`entity:<uuid>`), para pedir su detalle; null si no es una entidad. */
export const uuidDe = (ref: string): string | null => (/^entity:([0-9a-f-]{36})$/i.exec(ref)?.[1] ?? null);

/** La referencia de una persona o empresa como la pide la API, desde la `ref` del documento. */
export function sujetoDeRef(ref: string): { kind: string; id: string; runId?: string } | null {
  const actor = /^siger_actor:([^@]+)@(.+)$/.exec(ref);
  if (actor) return { kind: 'siger_actor', id: actor[1]!, runId: actor[2]! };
  const entidad = /^entity:(.+)$/.exec(ref);
  if (entidad) return { kind: 'entity', id: entidad[1]! };
  // Corte 4a de los certificados del SAT: un representante sin ficha confirmada, por su uuid y sin lectura del registro.
  const rep = /^sat_rep:(.+)$/.exec(ref);
  return rep ? { kind: 'sat_rep', id: rep[1]! } : null;
}
/** Corte 4a: la ficha propia de un representante ante el SAT sin ficha confirmada (clase sat_rep). */
export const esSatRep = (ref: string) => ref.startsWith('sat_rep:');
/** Corte 4b-1: el aviso de la ficha propia de un representante; si la lista lo tiene sólo como posible, el que lo dice (T2). */
export const avisoSatRep = (doc: Pick<Hallazgos, 'identidad_lista'>): string => doc.identidad_lista?.nivel === 'posible' ? SAT_REP.avisoPosible : SAT_REP.aviso;
